import { FC } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { format } from 'date-fns';

import { ActionConfirmation, Box } from '@qwealth/qcore';
import { S3Item } from '@qwealth/qvault';

import { Kyp } from 'data/models/Kyp';
import { TextButton } from './KypDetails.styled';
import { deleteAllKypFileAttachments, deleteKyp } from 'data/data-layer/kyp';
import { setKyps, setSelectedKyp } from 'data/actions/KYP';
import { selectKyps } from 'data/selectors/kypSelectors';

interface KypDetailsHeaderProps {
  kyp: Kyp;
  attachments: S3Item[];
  hasEditPermissions: boolean;
  clickEdit: () => void;
}

const KypDetailsHeader: FC<KypDetailsHeaderProps> = ({
  kyp,
  attachments,
  hasEditPermissions,
  clickEdit,
}): JSX.Element => {
  const kyps = useSelector(selectKyps);
  const dispatch = useDispatch();

  const clickDeleteKyp = async () => {
    const didDeleteAllAttachments = await deleteAllKypFileAttachments(attachments);
    if (!didDeleteAllAttachments) {
      return;
    }

    const deletedKyp = await deleteKyp(kyp, dispatch);
    if (deletedKyp !== undefined) {
      dispatch(setSelectedKyp(undefined));
      const kypsWithDeletedKypRemoved = kyps.filter(product => {
        return product.uuid !== kyp.uuid;
      });
      dispatch(setKyps(kypsWithDeletedKypRemoved));
    }
  };

  return (
    <Box paddingBottom="default">
      <Box display="flex" flexDirection="row" gap="large" alignItems="center">
        <Box fontSize="appTitle" fontWeight="bolder">
          {kyp.name}
        </Box>
        {hasEditPermissions && (
          <Box display="flex" flexDirection="row" gap="large" alignItems="center">
            <TextButton onClick={clickEdit} disabled={!hasEditPermissions}>
              Edit
            </TextButton>
            <ActionConfirmation
              actionHandler={() => {
                (async () => await clickDeleteKyp())();
              }}
              actionTitle={'Delete KYP Confirmation'}
              target={'delete ' + kyp.name}
              actionLabel={'Delete'}
              ActionButtonRenderer={openHandler => (
                <TextButton
                  isDeleteButton={true}
                  onClick={openHandler}
                  disabled={!hasEditPermissions}
                >
                  Delete
                </TextButton>
              )}
            />
          </Box>
        )}
      </Box>

      <Box fontSize="small" marginTop="default">
        <Box fontWeight="bold">
          Created By{' '}
          <Box as="span" fontWeight="normal">
            {kyp.createdBy}
            {' on '}
            {format(new Date(kyp.startTime), 'MMMM d, yyyy')}
          </Box>
        </Box>
        <Box fontWeight="bold">
          Last Updated By
          <Box as="span" fontWeight="normal">
            {' '}
            {kyp.lastUpdatedBy}
            {' on '}
            {format(new Date(kyp.lastUpdated), 'MMMM d, yyyy')}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default KypDetailsHeader;
